/**
 * ImpersonationBanner Component
 * Displays prominent banner when admin is impersonating a merchant.
 * Reads impersonation state from sessionStorage (set by admin pages).
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UserCheck } from 'lucide-react';

interface ImpersonationData {
  merchant_id: string;
  merchant_name: string;
  started_at: string;
}

export const ImpersonationBanner: React.FC = () => {
  const { t } = useTranslation();
  const [session, setSession] = useState<ImpersonationData | null>(null);

  useEffect(() => {
    const raw = sessionStorage.getItem('impersonation');
    if (raw) {
      try {
        setSession(JSON.parse(raw));
      } catch {
        // ignore
      }
    }
  }, []);

  if (!session) return null;

  const handleStop = () => {
    sessionStorage.removeItem('impersonation');
    const basePath = import.meta.env.VITE_BASE_PATH || '/';
    window.location.href = `${basePath}admin/merchants`;
  };

  return (
    <div className="bg-error text-error-content shadow-lg">
      <div className="container mx-auto px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <UserCheck className="h-5 w-5" />
          <div>
            <span className="font-semibold">
              {t('admin.impersonating', { name: session.merchant_name })}
            </span>
            <span className="text-sm opacity-90 ml-3">
              {t('admin.impersonationStarted', { time: new Date(session.started_at).toLocaleTimeString() })}
            </span>
          </div>
        </div>
        <button
          onClick={handleStop}
          className="btn btn-sm btn-ghost"
        >
          {t('admin.stopImpersonation')}
        </button>
      </div>
    </div>
  );
};
